import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Row } from "reactstrap";
import "../../../assets/css/gamesCss/Graph/introDirected.css"
import { Col } from "reactstrap"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import "../../../assets/css/colors.css"
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";


const initialNodes = [
    { id: "Η", label: "Ηome" },
    { id: "U", label: "University" },
    { id: "G", label: "Gym" },
    { id: "F", label: "Fun" },
    { id: "W", label: "Work" },
    { id: "A", label: "Airport" },
    { id: "B", label: "Beach" },
    { id: "C", label: "Cafe" },
];

const SVG_WIDTH = 700;
const SVG_HEIGHT = 450;
const NODE_SIZE = 50;


const DirectedGameIntro = () => {
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();


    const selectedGame = {
        description: selLanguage
            ? "Στόχος αυτού του παιχνιδιού είναι η αναπαράσταση ενός κατευθυνόμενου γράφου που απεικονίζει τις διαδρομές που ακολουθεί ένας φοιτητής.Ξεκινάει από το σπίτι του και πηγαίνει στο πανεπιστήμιο, μετά στη δουλειά, επιστρέφει στο σπίτι, πηγαίνει στο γυμναστήριο, μετά για διασκέδαση και τελικά επιστρέφει πάλι στο σπίτι. Χρησιμοποίησε τους διαθέσιμους κόμβους για να δημιουργήσεις τις σωστές κατευθυνόμενες ακμές που απεικονίζουν αυτή τη διαδρομή. H = Home, U = University, W = Work, G = Gym, F = Fun ,A = Airport, B = Beach, C = Cafe."
            : "The goal of this game is to represent a directed graph that illustrates the routes taken by a student. They start from their home and go to the university, then to work, return home, go to the gym, then for fun, and finally return home again. Use the available nodes to create the correct directed edges that depict this route. H = Home, U = University, W = Work, G = Gym, F = Fun ,A = Airport, B = Beach, C = Cafe."
    };

    // Nodes που είναι μέσα στο πλαίσιο
    const [nodes, setNodes] = useState([]);
    // Nodes που είναι έξω από το πλαίσιο (αρχικά όλα)
    const [availableNodes, setAvailableNodes] = useState(initialNodes);
    const [edges, setEdges] = useState([]);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [remainingEdges, setRemainingEdges] = useState(10);
    const [isDragging, setIsDragging] = useState(false);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);

    const [pathResult, setPathResult] = useState(null);
    const [lives, setLives] = useState(3);
    const [isChecking, setIsChecking] = useState(false);

    const { darkMode } = useDarkMode();
    // Check for game over when lives change
    useEffect(() => {
        if (lives <= 0) {
            setShowGameOver(true);
        }
    }, [lives]);

    // Όταν κάνεις drag ένα node από τα διαθέσιμα στο πλαίσιο
    const handleDropNode = (nodeId, x, y) => {
        const node = availableNodes.find((n) => n.id === nodeId);
        if (!node) return;

        // Convert screen coordinates to SVG coordinates
        const pos = {
            x: Math.max(0, Math.min(x - NODE_SIZE / 2, SVG_WIDTH - NODE_SIZE)),
            y: Math.max(0, Math.min(y - NODE_SIZE / 2, SVG_HEIGHT - NODE_SIZE)),
        };
        setNodes((prev) => [...prev, { ...node, ...pos }]);
        setAvailableNodes((prev) => prev.filter((n) => n.id !== nodeId));
    };

    const handleNodeClick = (nodeId) => {
        if (selectedNodes.includes(nodeId)) return;

        const newSelection = [...selectedNodes, nodeId];
        if (newSelection.length === 2) {
            const [from, to] = newSelection;

            if (
                !edges.find(
                    (edge) =>
                        (edge.from === from && edge.to === to) ||
                        (edge.from === to && edge.to === from)
                ) &&
                remainingEdges > 0
            ) {
                setEdges([...edges, { from, to }]);
                setRemainingEdges(remainingEdges - 1);
            }

            setSelectedNodes([]);
        } else {
            setSelectedNodes(newSelection);
        }
    };

    const getNodeById = (id) => nodes.find((n) => n.id === id);

    const checkDeliveryPath = async () => {
        setIsChecking(true);
        setPathResult(null);

        await new Promise(resolve => setTimeout(resolve, 500));

        // Έλεγχος 1: Έχουν τοποθετηθεί όλοι οι απαραίτητοι κόμβοι;
        const requiredNodes = ["Η", "U", "W", "G", "F"];
        const availableNodeIds = nodes.map(n => n.id);

        for (const nodeId of requiredNodes) {
            if (!availableNodeIds.includes(nodeId)) {
                setPathResult({
                    success: false,
                    message: selLanguage ? "Τοποθέτησε όλους τους απαραίτητους κόμβους." : "Place all the required nodes.",
                    foundPath: null
                });
                setLives(prev => prev - 1);
                setIsChecking(false);
                return;
            }
        }

        // Έλεγχος 2: Έχουν δημιουργηθεί οι σωστές ακμές;
        // Από το CORRECT_PATH ["Η", "U", "W", "Η", "G", "F", "Η"]
        // χρειαζόμαστε τις ακμές: Η→U, U→W, W→Η, Η→G, G→F, F→Η
        const requiredEdges = [
            { from: "Η", to: "U" },
            { from: "U", to: "W" },
            { from: "W", to: "Η" },
            { from: "Η", to: "G" },
            { from: "G", to: "F" },
            { from: "F", to: "Η" }
        ];

        let allEdgesExist = true;
        let missingEdge = null;

        for (const reqEdge of requiredEdges) {
            const edgeExists = edges.some(
                edge => edge.from === reqEdge.from && edge.to === reqEdge.to
            );

            if (!edgeExists) {
                allEdgesExist = false;
                missingEdge = reqEdge;
                break;
            }
        }

        if (!allEdgesExist) {
            setPathResult({
                success: false,
                message: selLanguage ? "Έλεγξε τις συνδέσεις σου." : "Check your connections.",
                foundPath: null
            });
            setLives(prev => prev - 1);
            setIsChecking(false);
            return;
        }

        // Αν όλα είναι σωστά
        setShowGameWon(true);
        setIsChecking(false);
    };
    // Αφαιρεί κόμβο από το πλαίσιο και τον επιστρέφει στα διαθέσιμα
    const removeNodeFromFrame = (nodeId) => {
        const nodeToRemove = nodes.find(n => n.id === nodeId);
        if (!nodeToRemove) return;

        // Αφαίρεση του κόμβου από το πλαίσιο
        setNodes(prev => prev.filter(n => n.id !== nodeId));

        // Επιστροφή στα διαθέσιμα
        setAvailableNodes(prev => [...prev, { id: nodeToRemove.id, label: nodeToRemove.label }]);

        // Αφαίρεση όλων των ακμών που συνδέονται με αυτόν τον κόμβο
        const removedEdgesCount = edges.filter(edge => edge.from === nodeId || edge.to === nodeId).length;
        setEdges(prev => prev.filter(edge => edge.from !== nodeId && edge.to !== nodeId));

        // Επιστροφή των ακμών
        setRemainingEdges(prev => prev + removedEdgesCount);

        // Καθαρισμός selection αν ήταν επιλεγμένος
        setSelectedNodes(prev => prev.filter(id => id !== nodeId));
    };

    // Drag & Drop από τα διαθέσιμα στο πλαίσιο
    const handleDragStartAvailable = (e, nodeId) => {
        e.dataTransfer.setData("nodeId", nodeId);
    };

    const handleDragOverFrame = (e) => e.preventDefault();

    const handleDropOnFrame = (e) => {
        e.preventDefault();
        const nodeId = e.dataTransfer.getData("nodeId");
        if (nodeId) {
            // Get SVG element and calculate relative position
            const svgElement = e.currentTarget.querySelector('svg');
            const rect = svgElement.getBoundingClientRect();
            const scaleX = SVG_WIDTH / rect.width;
            const scaleY = SVG_HEIGHT / rect.height;

            const x = (e.clientX - rect.left) * scaleX;
            const y = (e.clientY - rect.top) * scaleY;

            handleDropNode(nodeId, x, y);
        }
    };

    const resetGame = () => {
        setNodes([]);
        setAvailableNodes(initialNodes);
        setEdges([]);
        setSelectedNodes([]);
        setRemainingEdges(10);
        setPathResult(null);
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);

    };

    return (
        <div
            className='main-content introDirectedContainer'
            style={{
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
            }}
        >

            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}



            />
            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div className="max-w-7xl mx-auto">

                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            justifyContent: "right",
                            gap: "12px"
                        }}>


                            <div style={{
                                display: "flex",
                                gap: "6px",
                                alignItems: "center"
                            }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lives >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                            transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                            transition: "all 0.3s ease",
                                            animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>


                        </div>

                        <style>{`
                        @keyframes heartbeat {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                        }
                        @keyframes pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.5; }
                        }
                        .checking-animation {
                            animation: pulse 1s ease-in-out infinite;
                        }
                    `}</style>
                    </div>

                    <br />

                    <div className="introDirectedProm">
                        {selectedGame.description}
                    </div>

                    {/* Διαθέσιμοι κόμβοι */}
                    <div className="introDirectedLeftNodes">
                        {availableNodes.map((node) => (
                            <div
                                key={node.id}
                                draggable
                                onDragStart={(e) => handleDragStartAvailable(e, node.id)}
                                className="introDirectedNode"
                                title={`${node.label} - Σύρε με στο πλαίσιο`}
                            >
                                {node.id}
                            </div>
                        ))}
                    </div>

                    {/* Responsive SVG Frame */}
                    <div
                        className={`introDirectedFrame ${isChecking ? 'checking-animation' : ''}`}
                        onDragOver={handleDragOverFrame}
                        onDrop={handleDropOnFrame}
                    >
                        <svg
                            width="100%"
                            height="auto"
                            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                            style={{ maxWidth: "700px", width: "100%", height: "auto", display: "block" }}
                        >
                            <defs>
                                <marker
                                    id="arrowhead"
                                    markerWidth="8"
                                    markerHeight="6"
                                    refX="7"
                                    refY="3"
                                    orient="auto"
                                >
                                    <polygon
                                        points="0 0, 8 3, 0 6"
                                        fill="#3b82f6"
                                    />
                                </marker>
                                <filter id="glow">
                                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>

                            {/* Background grid */}
                            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e2e8f0" strokeWidth="1" opacity="0.3" />
                            </pattern>
                            <rect width="100%" height="100%" fill="url(#grid)" />

                            {edges.map((edge, idx) => {
                                const from = getNodeById(edge.from);
                                const to = getNodeById(edge.to);
                                if (!from || !to) return null;

                                // Υπολογισμός σημείων στο περίγραμμα των κόμβων για το βέλος
                                const fromCenterX = from.x + NODE_SIZE / 2;
                                const fromCenterY = from.y + NODE_SIZE / 2;
                                const toCenterX = to.x + NODE_SIZE / 2;
                                const toCenterY = to.y + NODE_SIZE / 2;

                                // Υπολογισμός κατεύθυνσης
                                const dx = toCenterX - fromCenterX;
                                const dy = toCenterY - fromCenterY;
                                const distance = Math.sqrt(dx * dx + dy * dy);

                                // Κανονικοποίηση και υπολογισμός σημείων στο περίγραμμα
                                const unitX = dx / distance;
                                const unitY = dy / distance;
                                const radius = NODE_SIZE / 2;

                                const startX = fromCenterX + unitX * radius;
                                const startY = fromCenterY + unitY * radius;
                                const endX = toCenterX - unitX * (radius + 4);
                                const endY = toCenterY - unitY * (radius + 4);

                                return (
                                    <line
                                        key={idx}
                                        x1={startX}
                                        y1={startY}
                                        x2={endX}
                                        y2={endY}
                                        stroke="#3b82f6"
                                        strokeWidth="4"
                                        strokeDasharray="8 6"
                                        markerEnd="url(#arrowhead)"
                                        filter="url(#glow)"
                                        opacity="0.8"
                                    />
                                );
                            })}

                            {nodes.map((node, index) => (
                                <g key={node.id}>
                                    {/* Σκιά κόμβου */}
                                    <motion.circle
                                        cx={node.x + NODE_SIZE / 2 + 2}
                                        cy={node.y + NODE_SIZE / 2 + 2}
                                        r={NODE_SIZE / 2 - 2}
                                        fill="rgba(0,0,0,0.2)"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                    />

                                    {/* Κύριος κόμβος */}
                                    <motion.circle
                                        cx={node.x + NODE_SIZE / 2}
                                        cy={node.y + NODE_SIZE / 2}
                                        r={NODE_SIZE / 2 - 2}
                                        fill={selectedNodes.includes(node.id)
                                            ? "#f97316"
                                            : "#3b82f6"}
                                        stroke="#1f2937"
                                        strokeWidth="3"
                                        style={{ cursor: 'pointer' }}
                                        initial={{ scale: 0, rotate: 0 }}
                                        animate={{ scale: 1 }}
                                        whileHover={{ scale: 1.15 }}
                                        whileTap={{ scale: 0.95 }}
                                        onMouseDown={() => handleNodeClick(node.id)}
                                        onDoubleClick={() => removeNodeFromFrame(node.id)}
                                    />

                                    {/* Label κόμβου */}
                                    <motion.text
                                        x={node.x + NODE_SIZE / 2}
                                        y={node.y + NODE_SIZE / 2 + 5}
                                        textAnchor="middle"
                                        fill="white"
                                        fontSize="18"
                                        fontWeight="700"
                                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                    >
                                        {node.id}
                                    </motion.text>
                                </g>
                            ))}


                        </svg>
                    </div>

                    {/* Κουμπία ελέγχου */}
                    <div style={{ textAlign: "center", display: "flex", justifyContent: "center", width: "100%" }}>
                        <Row>
                            <button
                                onClick={checkDeliveryPath}
                                disabled={isChecking}
                                style={{
                                    background: (isChecking)
                                        ? "linear-gradient(135deg, #64748b 0%, #475569 100%)"
                                        : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "16px",
                                    padding: "14px 28px",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: (isChecking) ? "not-allowed" : "pointer",
                                    boxShadow: (isChecking)
                                        ? "0 10px 25px rgba(100, 116, 139, 0.3)"
                                        : "0 10px 25px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)",
                                    transition: "all 0.3s ease",
                                    transform: "scale(1)",
                                    marginRight: "12px"
                                }}
                                onMouseEnter={(e) => {
                                    if (!isChecking) {
                                        e.target.style.transform = "scale(1.02)";
                                        e.target.style.boxShadow = "0 15px 35px rgba(59, 130, 246, 0.4), 0 0 0 2px rgba(59, 130, 246, 0.3)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isChecking) {
                                        e.target.style.transform = "scale(1)";
                                        e.target.style.boxShadow = "0 10px 25px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)";
                                    }
                                }}
                            >
                                {isChecking
                                    ? (selLanguage ? "Έλεγχος..." : "Checking...")
                                    : (selLanguage ? "Έλεγχος Αποστολής" : "Check Mission")
                                }
                            </button>

                            <button
                                onClick={resetGame}
                                style={{
                                    background: (isChecking)
                                        ? "linear-gradient(135deg, #64748b 0%, #475569 100%)"
                                        : "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "16px",
                                    padding: "14px 28px",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: (isChecking) ? "not-allowed" : "pointer",
                                    boxShadow: (isChecking)
                                        ? "0 10px 25px rgba(100, 116, 139, 0.3)"
                                        : "0 10px 25px rgba(184, 30, 30, 0.3), 0 0 0 1px rgba(184, 30, 30, 0.2)",
                                    transition: "all 0.3s ease",
                                    transform: "scale(1)"
                                }}
                                onMouseEnter={(e) => {
                                    if (!isChecking) {
                                        e.target.style.transform = "scale(1.02)";
                                        e.target.style.boxShadow = "0 15px 35px rgba(184, 30, 30, 0.4), 0 0 0 2px rgba(184, 30, 30, 0.3)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isChecking) {
                                        e.target.style.transform = "scale(1)";
                                        e.target.style.boxShadow = "0 10px 25px rgba(184, 30, 30, 0.3), 0 0 0 1px rgba(184, 30, 30, 0.2)";
                                    }
                                }}>
                                {selLanguage ? "Καθαρισμός" : "Reset"}
                            </button>
                        </Row>
                    </div>

                    {pathResult && (
                        <div
                            style={{
                                textAlign: "center",
                                padding: "20px",
                                borderRadius: "16px",
                                marginTop: "20px",
                                background: pathResult.success
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                border: `2px solid ${pathResult.success ? "#10b981" : "#ef4444"}`,
                                color: pathResult.success ? "#059669" : "#dc2626",
                                fontWeight: 600,
                                fontSize: "1rem",
                                boxShadow: pathResult.success
                                    ? "0 10px 25px rgba(16, 185, 129, 0.2)"
                                    : "0 10px 25px rgba(239, 68, 68, 0.2)"
                            }}
                        >
                            {pathResult.message}
                        </div>
                    )}

                </div>
            </Col>
        </div >
    );
};

export default DirectedGameIntro;